import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { useNotification } from '../../context/NotificationContext';
import { getMirrors, getNextMirrorId, getNextMirrorCode, createMirror, updateMirror, deleteMirror } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import BrandBranchFilterFields from '../../components/BrandBranchFilterFields';
import usePortalScope from '../../hooks/usePortalScope';
import { tenantListParams } from '../../utils/contentScope';
import { removeConfirmMessage } from '../../utils/portalCopy';
import './AdminPages.css';

const MirrorManagement = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const portal = usePortalScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const salons = portal.salons;
  const brands = portal.brands;
  const [showForm, setShowForm] = useState(false);
  const [editingMirror, setEditingMirror] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    brandId: portal.lockBrandId || '',
    salonId: portal.lockSalonId || ''
  });
  const [formData, setFormData] = useState({
    mirrorId: '',
    mirrorCode: '',
    salonId: portal.lockSalonId || '',
    brandId: portal.lockBrandId || '',
    linkToBranch: Boolean(portal.isBranchAdmin)
  });
  const [mirrorCodeHint, setMirrorCodeHint] = useState('');

  useEffect(() => {
    if (!portal.lockBrandId && !portal.lockSalonId) return;
    setFilters((prev) => {
      const brandId = portal.lockBrandId || prev.brandId || '';
      const salonId = portal.lockSalonId || prev.salonId || '';
      if (prev.brandId === brandId && prev.salonId === salonId) return prev;
      return { ...prev, brandId, salonId };
    });
  }, [portal.lockBrandId, portal.lockSalonId]);

  const searchKey = useMemo(
    () => JSON.stringify({ searchTerm, ...filters }),
    [searchTerm, filters]
  );

  const getMirrorListParams = useCallback(() => {
    const params = { ...tenantListParams(portal, filters) };
    if (searchTerm) params.search = searchTerm;
    return params;
  }, [searchTerm, filters, portal]);

  const { items: mirrors, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: getMirrors,
    resetKey: searchKey,
    getParams: getMirrorListParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(mirrors, (mirror) => mirror._id, {
    defaultView: 'grid',
    resetKey: searchKey
  });

  const suggestBrandCode2 = (name) => {
    const words = String(name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const a = (words[0]?.[0] || '').toUpperCase();
    const b = (words[1]?.[0] || words[0]?.[1] || '').toUpperCase();
    const raw = `${a}${b}`.replace(/[^A-Z0-9]/g, '');
    return (raw || 'BC').padEnd(2, 'X').slice(0, 2);
  };

  const suggestBranchSuffix3 = (value) => {
    const v = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return (v.slice(0, 3) || 'BRN').padEnd(3, 'X').slice(0, 3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      delete payload.brandId; // UI-only (brand is derived from selected branch)
      delete payload.linkToBranch; // UI-only
      if (!formData.linkToBranch) {
        delete payload.salonId;
        delete payload.mirrorCode;
      }
      if (editingMirror) {
        await updateMirror(editingMirror._id, payload);
      } else {
        await createMirror(payload);
      }
      setShowForm(false);
      setEditingMirror(null);
      resetForm();
      showSuccess(editingMirror ? 'Mirror updated.' : 'Mirror created.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || `Failed to ${editingMirror ? 'update' : 'create'} mirror`);
    }
  };

  const resetForm = () => {
    setFormData({
      mirrorId: '',
      mirrorCode: '',
      salonId: portal.lockSalonId || '',
      brandId: portal.lockBrandId || '',
      linkToBranch: Boolean(portal.isBranchAdmin)
    });
    setMirrorCodeHint('');
  };

  const handleEdit = async (mirror) => {
    let salonId = typeof mirror.salon === 'object' ? mirror.salon._id : mirror.salon || '';
    const hasVendorCode = mirrorHasVendorCode(mirror);
    let brandId = typeof mirror.salon === 'object' ? mirror.salon?.brand?._id || '' : '';
    let linkToBranch = Boolean(mirror.salon);
    if (portal.isBranchAdmin) {
      linkToBranch = true;
      salonId = salonId || portal.lockSalonId || '';
      brandId = brandId || portal.lockBrandId || '';
    } else if (portal.lockBrandId && !brandId) {
      brandId = portal.lockBrandId;
    }
    setEditingMirror(mirror);
    setFormData({
      mirrorId: mirror.mirrorId || '',
      mirrorCode: mirror.mirrorCode || '',
      salonId,
      brandId,
      linkToBranch
    });
    setMirrorCodeHint('');
    setShowForm(true);
    if (salonId && !hasVendorCode) {
      await suggestMirrorCodeForBranch(salonId);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Remove mirror',
      message: removeConfirmMessage('mirror'),
      confirmText: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await deleteMirror(id);
      showSuccess('Mirror removed.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove mirror');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMirror(null);
    resetForm();
  };

  const openAddForm = async () => {
    setEditingMirror(null);
    resetForm();
    setShowForm(true);
    // Suggest internal Mirror ID immediately (can create without linking).
    try {
      const res = await getNextMirrorId();
      const nextMirrorId = res?.data?.mirrorId;
      if (nextMirrorId) {
        setFormData((prev) => ({ ...prev, mirrorId: nextMirrorId }));
      }
    } catch (_e) {
      // ignore; mirrorId will still be generated server-side
    }
    if (portal.isBranchAdmin && portal.lockSalonId) {
      await suggestMirrorCodeForBranch(portal.lockSalonId);
    }
  };

  const openedFromQuery = useRef(false);
  useEffect(() => {
    if (openedFromQuery.current) return;
    if (searchParams.get('new') !== '1') return;
    openedFromQuery.current = true;
    openAddForm();
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const suggestMirrorCodeForBranch = async (salonId) => {
    if (!salonId) return;
    try {
      setMirrorCodeHint('Generating mirror code…');
      const selected = salons.find((s) => String(s._id) === String(salonId));
      const brandCode =
        String(selected?.brand?.brandCode || '').trim().toUpperCase() ||
        suggestBrandCode2(selected?.brand?.name);
      const branchCode =
        String(selected?.branchCode || '').trim().toUpperCase() ||
        suggestBranchSuffix3(selected?.branch || selected?.name);

      const res = await getNextMirrorCode({ brandCode, branchCode });
      const nextMirrorCode = res?.data?.mirrorCode;
      const errorMsg = res?.data?.error || '';
      if (nextMirrorCode) {
        setFormData((prev) => ({ ...prev, mirrorCode: nextMirrorCode }));
        setMirrorCodeHint('');
      } else {
        const msg = errorMsg || 'Unable to generate mirror code. Ensure Branch ID and Brand Code are set.';
        setMirrorCodeHint(msg);
        showError(msg);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Failed to auto-generate mirror code';
      setMirrorCodeHint(msg);
      showError(msg);
    }
  };

  const mirrorHasVendorCode = (mirror) => Boolean(String(mirror?.mirrorCode || '').trim());
  const canSuggestMirrorCode = !editingMirror || !mirrorHasVendorCode(editingMirror);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      brandId: portal.lockBrandId || '',
      salonId: portal.lockSalonId || ''
    });
  };

  const mirrorColumns = useMemo(
    () => [
      {
        key: 'mirror',
        label: 'Mirror',
        render: (mirror) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong>{mirror.mirrorCode || mirror.mirrorId}</strong>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: mirror.hasActiveSession ? '#2EC920' : '#F04A4A',
                boxShadow: mirror.hasActiveSession
                  ? '0 0 6px rgba(46,201,32,0.8)'
                  : '0 0 4px rgba(240,74,74,0.7)'
              }}
              title={mirror.hasActiveSession ? 'Active session on this mirror' : 'No active session'}
            />
          </div>
        )
      },
      { key: 'mirrorId', label: 'Unique ID', render: (mirror) => mirror.mirrorId || '-' },
      { key: 'branch', label: 'Branch', render: (mirror) => mirror.salon?.name || 'N/A' },
      {
        key: 'session',
        label: 'Session',
        render: (mirror) => (mirror.hasActiveSession ? 'Active' : 'Inactive')
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (mirror) => (
          <div className="table-actions">
            <button type="button" className="btn btn-sm btn-primary" onClick={() => handleEdit(mirror)}>
              Edit
            </button>
            <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(mirror._id)}>
              Remove
            </button>
          </div>
        )
      }
    ],
    []
  );

  const mirrorListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && mirrors.length > 0 && (
        <p className="admin-infinite-end">All mirrors loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const mirrorEmptyState = (
    <div className="empty-state">
      <p>No mirrors found. Create your first mirror!</p>
    </div>
  );

  return (
    <AdminLayout
      pageTitle="Branch & Mirror Management"
      subtitle={
        pagination.total
          ? `${mirrors.length} of ${pagination.total} mirrors · scroll for more`
          : 'Manage Alter Mirror devices by branch'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <div className="admin-page-header-title-row">
            <h1 style={{ margin: 0 }}>Mirror List</h1>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search mirrors by ID, code, branch..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button 
            className="btn btn-primary" 
            onClick={openAddForm}
          >
            + Add Mirror
          </button>
        </div>

        {(portal.showBrandPicker || portal.showBranchPicker) && (
        <div className="admin-card filter-card">
          <h3>Filters</h3>
          <div className="filter-grid">
            <BrandBranchFilterFields
              portal={portal}
              filters={filters}
              onChange={handleFilterChange}
              brands={brands}
              salons={salons}
            />
            <div className="form-group filter-actions">
              <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>
        )}

      {loading ? (
        <Loader variant="dark" fullScreen={false} />
      ) : (
        <>
      {showForm && (
        <div className="admin-card" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, fontSize: '16px', color: '#FFFFFF', marginBottom: '20px' }}>
            {editingMirror ? 'Edit Mirror' : 'Add Mirror'}
          </h2>
          <p style={{ color: '#8189A5', fontSize: '12px', marginBottom: '16px' }}>
            Hierarchy: Brand → Branch → Mirrors. Assign this mirror to a branch.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Mirror Unique ID</label>
              <input
                type="text"
                className="admin-field-readonly"
                value={formData.mirrorId}
                readOnly
                placeholder="Auto-generated (e.g., MIR001)"
              />
              <small style={{ color: '#8189A5', fontSize: '12px' }}>
                Auto-generated internal ID. Assigned when you open this form and cannot be changed.
              </small>
            </div>

            <div className="form-group">
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: portal.isBranchAdmin ? 'default' : 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(formData.linkToBranch)}
                  disabled={Boolean(portal.isBranchAdmin)}
                  onChange={async (e) => {
                    const linkToBranch = e.target.checked;
                    setFormData((prev) => ({
                      ...prev,
                      linkToBranch,
                      ...(linkToBranch
                        ? {
                            brandId: prev.brandId || portal.lockBrandId || '',
                            salonId: prev.salonId || portal.lockSalonId || ''
                          }
                        : {
                            brandId: portal.lockBrandId || '',
                            salonId: portal.lockSalonId || '',
                            mirrorCode: ''
                          })
                    }));
                    if (!linkToBranch) {
                      setMirrorCodeHint('');
                    }
                  }}
                  style={{ width: 'auto' }}
                />
                Link to Brand / Branch (optional)
              </label>
              <small style={{ color: '#8189A5', fontSize: '12px' }}>
                When linked, a vendor-friendly Mirror Code (e.g., SP-KOR-001) is generated and can be shown on QR.
              </small>
            </div>

            {formData.linkToBranch ? (
              <>
                {!portal.isBranchAdmin && (
                <div className="form-group">
                  <label>Brand</label>
                  <select
                    value={formData.brandId}
                    disabled={Boolean(portal.lockBrandId)}
                    onChange={(e) => {
                      const brandId = e.target.value;
                      // Reset downstream selection so the branch list is consistent with brand.
                      setFormData((prev) => ({
                        ...prev,
                        brandId,
                        salonId: portal.lockSalonId || '',
                        mirrorCode: canSuggestMirrorCode ? '' : prev.mirrorCode
                      }));
                    }}
                  >
                    <option value="">All Brands</option>
                    {brands.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}{b.brandCode ? ` (${b.brandCode})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                )}

                <div className="form-group">
                  <label>Branch</label>
                  <select
                    value={formData.salonId}
                    disabled={Boolean(portal.lockSalonId)}
                    onChange={async (e) => {
                      const salonId = e.target.value;
                      const selected = salons.find((s) => String(s._id) === String(salonId));
                      const selectedBrandId = selected?.brand?._id ? String(selected.brand._id) : '';
                      setFormData((prev) => ({
                        ...prev,
                        salonId,
                        brandId: prev.brandId || selectedBrandId || portal.lockBrandId || '',
                        mirrorCode: canSuggestMirrorCode ? '' : prev.mirrorCode
                      }));
                      if (!salonId) return;
                      if (canSuggestMirrorCode) {
                        await suggestMirrorCodeForBranch(salonId);
                      }
                    }}
                  >
                    <option value="">Select Branch</option>
                    {salons
                      .filter((s) => !formData.brandId || String(s.brand?._id) === String(formData.brandId))
                      .map((salon) => (
                        <option key={salon._id} value={salon._id}>
                          {salon.name}
                          {salon.branchCode ? ` (${salon.branchCode})` : ''}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Mirror Code</label>
                  <input
                    type="text"
                    className="admin-field-readonly"
                    value={formData.mirrorCode}
                    readOnly
                    placeholder="Auto-generated after selecting Branch (e.g., SP-KOR-001)"
                  />
                  {mirrorCodeHint ? (
                    <small style={{ color: '#8189A5', fontSize: '12px' }}>{mirrorCodeHint}</small>
                  ) : (
                    <small style={{ color: '#8189A5', fontSize: '12px' }}>
                      Auto-generated from brand and branch. Select a branch to populate.
                    </small>
                  )}
                </div>
              </>
            ) : null}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingMirror ? 'Update Mirror' : 'Create Mirror'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <AdminListViewControls
        viewMode={listView.viewMode}
        onViewModeChange={listView.setViewMode}
        selectedCount={listView.selectedCount}
        onSelectAll={listView.selectAll}
        onClearSelection={listView.clearSelection}
        allSelected={listView.allSelected}
        someSelected={listView.someSelected}
        totalCount={mirrors.length}
            showSelection={false}
      />
      <AdminSelectableList
        items={mirrors}
        getItemId={(mirror) => mirror._id}
        viewMode={listView.viewMode}
            selectable={false}
        isSelected={listView.isSelected}
        onToggleItem={listView.toggleItem}
        allSelected={listView.allSelected}
        someSelected={listView.someSelected}
        onSelectAll={listView.selectAll}
        onClearSelection={listView.clearSelection}
        columns={mirrorColumns}
        tableClassName="mirror-table"
        renderGridCard={(mirror) => (
          <div className="admin-card">
            <div className="card-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3>Mirror: {mirror.mirrorCode || mirror.mirrorId}</h3>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: mirror.hasActiveSession ? '#2EC920' : '#F04A4A',
                      boxShadow: mirror.hasActiveSession
                        ? '0 0 6px rgba(46,201,32,0.8)'
                        : '0 0 4px rgba(240,74,74,0.7)'
                    }}
                    title={mirror.hasActiveSession ? 'Active session on this mirror' : 'No active session'}
                  />
                </div>
                <p><strong>Branch:</strong> {mirror.salon?.name || 'N/A'}</p>
              </div>
              <div className="card-actions">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleEdit(mirror)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(mirror._id)}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
        emptyState={mirrorEmptyState}
        scrollRef={scrollRef}
        footer={mirrorListFooter}
      />
      </>
      )}
      </div>
    </AdminLayout>
  );
};

export default MirrorManagement;
